import React from "react";
import {
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ScheduleOutlined,
  PieChartOutlined,
  FileProtectOutlined,
  GiftOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  SolutionOutlined,
  UserDeleteOutlined,
  ApartmentOutlined,
  ShopOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import config from "../../permission/config.json";

// This is a curated VIEW over destinations already permission-checked in
// QUICK_LINKS (./index.js) and MODULE_SEARCH_INDEX (./moduleSearchIndex.js) -
// it is NOT a new route source. Every {route, resource, roles} triple below
// is copied verbatim from one of those two files (each entry says which).
// The "bucket" key only decides which role sees which suggestions up front -
// the actual access gate for each entry is still the identical resource+roles
// check used everywhere else in Global Search, re-applied in
// getSuggestedSearchesForViewer() below. So an entry that isn't safe/visible
// for a given viewer in Quick Links (e.g. Leave Balance or Holidays for a
// plain Employee - see index.js's QUICK_LINKS comment for why) is filtered
// out here the exact same way, even though it's listed in that viewer's
// bucket for spec-completeness.

const ADMIN_SCOPE_ROLES = ["Admin", "HR", "Reporting Manager", "Assistance Reporting Manager"];

export const ROLE_SUGGESTED_SEARCHES = {
  Employee: [
    // QUICK_LINKS "My Attendance"
    {
      label: "My Attendance",
      route: "/user-reports/attendence-reports/attendence-register",
      icon: <ClockCircleOutlined />,
      color: "#0891B2",
      resource: config.RESOURCES.ATTENDANCE_REPORT,
    },
    // QUICK_LINKS "My Leave"
    {
      label: "My Leave",
      route: "/leave-application",
      icon: <CalendarOutlined />,
      color: "#059669",
      resource: config.RESOURCES.LEAVE,
    },
    // QUICK_LINKS "Leave Balance" - admin-scope gated there (no safe self-view
    // exists yet), kept identical so this only renders for a viewer who also
    // passes ADMIN_SCOPE_ROLES.
    {
      label: "My Leave Balance",
      route: "/leave-reports/leave-balance",
      icon: <PieChartOutlined />,
      color: "#0284C7",
      resource: config.RESOURCES.LEAVE_REPORT,
      roles: ADMIN_SCOPE_ROLES,
    },
    // QUICK_LINKS "Holidays" - Admin/HR gated there (the /holidays route IS
    // the admin CRUD page, no separate read-only list exists), kept identical.
    {
      label: "Company Holidays",
      route: "/holidays",
      icon: <GiftOutlined />,
      color: "#DB2777",
      resource: config.RESOURCES.HOLIDAYS,
      roles: ["Admin", "HR"],
    },
    // QUICK_LINKS "Policies"
    {
      label: "Company Policies",
      route: "/policies",
      icon: <FileProtectOutlined />,
      color: "#4F46E5",
      resource: config.RESOURCES.POLICIES,
    },
    // MODULE_SEARCH_INDEX "my-tickets"
    {
      label: "My Tickets",
      route: "/tickets",
      icon: <CustomerServiceOutlined />,
      color: "#CA8A04",
      resource: config.RESOURCES.TICKET,
    },
  ],

  Manager: [
    // QUICK_LINKS "Attendance Register"
    {
      label: "Team Attendance",
      route: "/attendence-reports/attendence-register",
      icon: <ClockCircleOutlined />,
      color: "#2563EB",
      resource: config.RESOURCES.ATTENDANCE_REPORT,
      roles: ADMIN_SCOPE_ROLES,
    },
    // QUICK_LINKS "Leave Approvals"
    {
      label: "Leave Approvals",
      route: "/admin-leave-approval",
      icon: <CalendarOutlined />,
      color: "#16A34A",
      resource: config.RESOURCES.ADMIN_LEAVE_MANAGEMENT,
    },
    // QUICK_LINKS "Leave Balance"
    {
      label: "Team Leave Balance",
      route: "/leave-reports/leave-balance",
      icon: <PieChartOutlined />,
      color: "#0284C7",
      resource: config.RESOURCES.LEAVE_REPORT,
      roles: ADMIN_SCOPE_ROLES,
    },
    // MODULE_SEARCH_INDEX "my-approvals"
    {
      label: "Pending Approvals",
      route: "/my-approvals",
      icon: <CheckCircleOutlined />,
      color: "#0D9488",
      resource: config.RESOURCES.APPROVAL_INBOX,
    },
    // MODULE_SEARCH_INDEX "expense-approvals"
    {
      label: "Expense Approvals",
      route: "/expense/approvals",
      icon: <DollarOutlined />,
      color: "#CA8A04",
      resource: config.RESOURCES.EXPENSE_APPROVAL,
      roles: ["Admin", "HR", "Reporting Manager", "Assistance Reporting Manager"],
    },
  ],

  HR: [
    // QUICK_LINKS "Employees"
    {
      label: "Employee Directory",
      route: "/viewEmployeeDetails",
      icon: <UserOutlined />,
      color: "#7C3AED",
      resource: config.RESOURCES.EMPLOYEE_DETAILS,
    },
    // QUICK_LINKS "Attendance Register"
    {
      label: "Attendance Register",
      route: "/attendence-reports/attendence-register",
      icon: <ClockCircleOutlined />,
      color: "#2563EB",
      resource: config.RESOURCES.ATTENDANCE_REPORT,
      roles: ADMIN_SCOPE_ROLES,
    },
    // QUICK_LINKS "Leave Approvals" - also covers the spec's "Leave
    // Management" item, no separate verified Leave Management route exists.
    {
      label: "Leave Approvals",
      route: "/admin-leave-approval",
      icon: <CalendarOutlined />,
      color: "#16A34A",
      resource: config.RESOURCES.ADMIN_LEAVE_MANAGEMENT,
    },
    // MODULE_SEARCH_INDEX "onboarding"
    {
      label: "Onboarding",
      route: "/onboarding-initiation",
      icon: <SolutionOutlined />,
      color: "#7C3AED",
      resource: config.RESOURCES.ON_BOARDING_INITIATION,
      roles: ["Admin", "HR"],
    },
    // MODULE_SEARCH_INDEX "resignation-management"
    {
      label: "Separation",
      route: "/resignation-employee-list",
      icon: <UserDeleteOutlined />,
      color: "#DC2626",
      resource: config.RESOURCES.SEPARATION_MANAGEMENT,
      roles: ["Admin", "HR"],
    },
  ],

  Admin: [
    // MODULE_SEARCH_INDEX "employee-management"
    {
      label: "Employee Management",
      route: "/employee-master",
      icon: <UserOutlined />,
      color: "#7C3AED",
      resource: config.RESOURCES.EMPLOYEE_MASTER,
      roles: ["Admin", "HR"],
    },
    // MODULE_SEARCH_INDEX "department-management"
    {
      label: "Department Management",
      route: "/admin-department",
      icon: <ApartmentOutlined />,
      color: "#0E7490",
      resource: config.RESOURCES.DEPARTMENTS,
      roles: ["Admin", "HR"],
    },
    // MODULE_SEARCH_INDEX "branch-management"
    {
      label: "Branch Management",
      route: "/admin-branch",
      icon: <ShopOutlined />,
      color: "#0D9488",
      resource: config.RESOURCES.BRANCH_OPTIONS,
      roles: ["Admin", "HR"],
    },
    // MODULE_SEARCH_INDEX "designation-management"
    {
      label: "Designation Management",
      route: "/admin-designation",
      icon: <TagsOutlined />,
      color: "#9333EA",
      resource: config.RESOURCES.DESIGNATION,
      roles: ["Admin", "HR"],
    },
    // QUICK_LINKS "Work Week & Shifts"
    {
      label: "Shift Management",
      route: "/admin-shift",
      icon: <ScheduleOutlined />,
      color: "#D97706",
      resource: config.RESOURCES.SHIFT,
      roles: ["Admin", "HR"],
    },
    // QUICK_LINKS "System Settings"
    {
      label: "Settings",
      route: "/admin/settings",
      icon: <SettingOutlined />,
      color: "#6B7280",
      resource: config.RESOURCES.SETTINGS,
      roles: ["Admin", "HR"],
    },
    // MODULE_SEARCH_INDEX "payroll"
    {
      label: "Payroll",
      route: "/admin/zoho-payroll-integration",
      icon: <DollarOutlined />,
      color: "#CA8A04",
      resource: null,
      roles: ["Admin"],
    },
  ],
};

// Picks this viewer's bucket (most-privileged match wins, same ladder as
// ADMIN_SCOPE_ROLES elsewhere in Global Search), then filters that bucket's
// list with the exact same resource+roles check visibleQuickLinks uses in
// index.js, capped at 6 entries per the product spec ("show 4-6 initially").
export const getSuggestedSearchesForViewer = () => {
  let bucket;
  if (getRoles(["Admin"])) bucket = "Admin";
  else if (getRoles(["HR"])) bucket = "HR";
  else if (getRoles(["Reporting Manager", "Assistance Reporting Manager"])) bucket = "Manager";
  else bucket = "Employee";

  const list = ROLE_SUGGESTED_SEARCHES[bucket] || [];
  return list
    .filter(
      (entry) => (!entry.resource || getResource(entry.resource, ["read"])) && (!entry.roles || getRoles(entry.roles))
    )
    .slice(0, 6);
};
